import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, ArrowLeft } from 'lucide-react';

const Terms = () => {
  const lastUpdated = 'February 16, 2026';

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-orange-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-sm border p-8 md:p-12">
          <div className="flex items-center gap-3 mb-2">
            <ScrollText className="h-8 w-8 text-orange-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Terms & Conditions</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p>
                These Terms & Conditions ("Terms") govern your use of <strong>hivizprokenya.co.ke</strong> ("the Website")
                and the purchase of Personal Protective Equipment ("Products") from <strong>Hi-Viz Pro Kenya Limited</strong>,
                a company incorporated in Kenya ("Company", "we", "us", "our"). By accessing the Website or placing an order,
                you ("Customer", "you") agree to be bound by these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligibility</h2>
              <p>
                You must be at least 18 years old or have legal capacity to contract under the laws of Kenya. Orders placed
                on behalf of a company or organisation imply that you are authorised to bind that entity.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Products & Compliance</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All Products comply with applicable DOSHS and KEBS standards as displayed on the relevant product page.</li>
                <li>Specifications, images and certifications are provided in good faith. Minor variations may occur between batches.</li>
                <li>Customers are responsible for verifying that the Product suits their specific work environment and risk assessment.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Pricing & Payment</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All prices are listed in Kenya Shillings (KES) and are inclusive of applicable taxes unless stated otherwise.</li>
                <li>We accept online card payments (processed securely by Stripe) and Cash on Delivery within select locations.</li>
                <li>Bulk discounts apply automatically at checkout based on order quantity.</li>
                <li>We reserve the right to correct pricing errors and to refuse or cancel any order at our discretion.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Order Acceptance</h2>
              <p>
                An order is only confirmed once payment is received (for online orders) or once we accept the order in writing
                (for Cash on Delivery and B2B invoices). We may decline orders due to stock unavailability, suspected fraud, or
                pricing errors.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Delivery</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Delivery times are estimates and not guaranteed. Standard delivery within Nairobi: 1-3 business days. Other counties: 3-7 business days.</li>
                <li>Risk of loss and title for the Products pass to you upon delivery.</li>
                <li>You are responsible for providing accurate delivery information. Failed deliveries due to incorrect addresses may incur re-delivery charges.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
              <p>
                All content on the Website (text, images, logos, designs) is the property of Hi-Viz Pro Kenya Limited or its
                licensors and is protected by Kenyan and international copyright laws. You may not reproduce, distribute, or
                modify any content without prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, the Company's total liability for any claim arising out of or in
                connection with the purchase or use of a Product is limited to the price paid for that Product. We are not
                liable for indirect, incidental, or consequential damages.
              </p>
              <p className="mt-3">
                <strong>Important:</strong> PPE products are part of a broader workplace safety programme. The Company is not
                liable for injuries arising from misuse, improper fit, lack of training, or use outside the manufacturer's
                specifications.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Governing Law & Jurisdiction</h2>
              <p>
                These Terms are governed by the laws of the Republic of Kenya. Any dispute shall be subject to the exclusive
                jurisdiction of the courts of Nairobi, Kenya.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to These Terms</h2>
              <p>
                We may update these Terms at any time. The current version is always available on this page with the "Last
                updated" date above. Continued use of the Website after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact</h2>
              <p>For any questions regarding these Terms, please contact us:</p>
              <ul className="list-none pl-0 space-y-1 mt-3">
                <li><strong>Email:</strong> <a href="mailto:info@hivizprokenya.co.ke" className="text-orange-600 hover:underline">info@hivizprokenya.co.ke</a></li>
                <li><strong>Phone / WhatsApp:</strong> +254 716 504 759</li>
                <li><strong>Address:</strong> Nairobi, Kenya</li>
              </ul>
            </section>

            <div className="border-t pt-6 mt-8 text-sm text-gray-500">
              <p>
                See also: <Link to="/refund-policy" className="text-orange-600 hover:underline">Refund Policy</Link>{' · '}
                <Link to="/privacy" className="text-orange-600 hover:underline">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
